/**
 * Admin Sales History — Full branch-level sales history view.
 *
 * Features:
 * - Month selector (prev/next)
 * - Branch filter dropdown
 * - Calendar heatmap: green=entered, red=missed, gray=future
 * - Full entry table with backfill badges
 * - Summary stats: total entered, total missed days, compliance %
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { User } from '../../domain/entities';
import { cn } from '../../ui/utils';
import { isRemoteMode } from '../../config/repository';
import { apiFetch } from '../../data/ApiModuleRepository';
import {
  ChevronLeft, ChevronRight, Calendar, DollarSign, Clock,
  AlertTriangle, Loader2, Filter, CheckCircle2, XCircle,
  TrendingUp, ArrowLeft,
} from 'lucide-react';
import { motion } from 'framer-motion';

function formatCurrency(n: number): string {
  return n.toLocaleString('en-QA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

interface HistoryEntry {
  id: string;
  location_id: string;
  location_name: string;
  entry_date: string;
  sales_amount: number;
  entered_by: string;
  entered_name: string;
  created_at: string;
  brand_id: string;
  branch_id: string;
  brand_name?: string;
  branch_name?: string;
}

interface LocationInfo {
  id: string;
  name: string;
  brand_id: string;
  branch_id: string;
  daily_target: number;
  monthly_target: number;
  brand_name?: string;
  branch_name?: string;
}

interface BrandInfo { id: string; name: string; }
interface BranchInfo { id: string; name: string; brand_id: string; }

export function AdminSalesHistory({ user, onBack }: { user: User | null; onBack?: () => void }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [locations, setLocations] = useState<LocationInfo[]>([]);
  const [brands, setBrands] = useState<BrandInfo[]>([]);
  const [allBranches, setAllBranches] = useState<BranchInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [selectedLoc, setSelectedLoc] = useState<string>('all');

  const loadHistory = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (selectedBrand !== 'all') params.set('brandId', selectedBrand);
      if (selectedBranch !== 'all') params.set('branchId', selectedBranch);
      if (selectedLoc !== 'all') params.set('locationId', selectedLoc);
      const res = await apiFetch(`/api/map/sales/admin-history?${params.toString()}`);
      const d = (res as any).data ?? {};
      setEntries(d.entries ?? []);
      setLocations(d.locations ?? []);
      setBrands(d.brands ?? []);
      setAllBranches(d.branches ?? []);
    } catch (e) { console.error('[AdminSalesHistory]', e); }
    finally { setLoading(false); }
  }, [month, selectedBrand, selectedBranch, selectedLoc]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Reset child filters when parent changes
  const handleBrandChange = (val: string) => {
    setSelectedBrand(val);
    setSelectedBranch('all');
    setSelectedLoc('all');
  };
  const handleBranchChange = (val: string) => {
    setSelectedBranch(val);
    setSelectedLoc('all');
  };

  // Branches filtered by selected brand (for filter dropdown)
  const filteredBranches = selectedBrand === 'all' ? allBranches : allBranches.filter(b => b.brand_id === selectedBrand);

  // Month navigation
  const changeMonth = (dir: -1 | 1) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  const isCurrentMonth = month === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Filter entries
  const filteredEntries = selectedLoc === 'all' ? entries : entries.filter(e => e.location_id === selectedLoc);

  // Build calendar data
  const [yr, mn] = month.split('-').map(Number);
  const daysInMonth = new Date(yr, mn, 0).getDate();
  const todayDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Which locations to check for missing days
  const locsToCheck = selectedLoc === 'all' ? locations : locations.filter(l => l.id === selectedLoc);

  // Build day → entries map
  const dayMap: Record<string, HistoryEntry[]> = {};
  for (const e of filteredEntries) {
    if (!dayMap[e.entry_date]) dayMap[e.entry_date] = [];
    dayMap[e.entry_date].push(e);
  }

  // Stats
  const totalSales = filteredEntries.reduce((s, e) => s + Number(e.sales_amount ?? 0), 0);
  const daysWithEntries = new Set(filteredEntries.map(e => e.entry_date)).size;
  const maxCheckDay = isCurrentMonth ? now.getDate() : daysInMonth;
  const expectedEntries = locsToCheck.length * maxCheckDay;

  // Missing days: for each location, which days (up to today) have no entry
  const missingDays: { locName: string; date: string }[] = [];
  for (const loc of locsToCheck) {
    for (let d = 1; d <= maxCheckDay; d++) {
      const dateStr = `${month}-${String(d).padStart(2, '0')}`;
      const hasEntry = filteredEntries.some(e => e.location_id === loc.id && e.entry_date === dateStr);
      if (!hasEntry) missingDays.push({ locName: loc.name, date: dateStr });
    }
  }

  const actualEntries = filteredEntries.length;
  const complianceRate = expectedEntries > 0 ? Math.round((actualEntries / expectedEntries) * 100) : 0;
  const backfillCount = filteredEntries.filter(e => {
    const createdStr = e.created_at ? String(e.created_at).slice(0, 10) : '';
    return createdStr && createdStr !== e.entry_date;
  }).length;

  // Calendar cells
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dateStr = `${month}-${String(day).padStart(2, '0')}`;
    const isFuture = isCurrentMonth && day > now.getDate();
    const isToday = dateStr === todayDate;
    const dayEntries = dayMap[dateStr] ?? [];
    const daySales = dayEntries.reduce((s, e) => s + Number(e.sales_amount ?? 0), 0);
    const expectedCount = locsToCheck.length;
    const actualCount = new Set(dayEntries.map(e => e.location_id)).size;
    const allFilled = actualCount >= expectedCount && expectedCount > 0;
    const someFilled = actualCount > 0;
    return { day, dateStr, isFuture, isToday, daySales, dayEntries, allFilled, someFilled, actualCount, expectedCount };
  });

  // First day of month offset (0 = Sunday)
  const firstDayOfWeek = new Date(yr, mn - 1, 1).getDay();

  return (
    <div className="space-y-5">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              <ArrowLeft size={16} className="text-slate-600" />
            </button>
          )}
          <div>
            <h2 className="text-lg font-black text-slate-900">Sales History</h2>
            <p className="text-xs text-slate-400">Track daily entries, missed days, and branch compliance</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Brand filter */}
          {brands.length > 1 && (
            <select value={selectedBrand} onChange={e => handleBrandChange(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 appearance-none cursor-pointer hover:border-slate-300 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none">
              <option value="all">All Brands</option>
              {brands.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}

          {/* Branch filter */}
          {filteredBranches.length > 1 && (
            <select value={selectedBranch} onChange={e => handleBranchChange(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 appearance-none cursor-pointer hover:border-slate-300 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none">
              <option value="all">All Branches</option>
              {filteredBranches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}

          {/* Store/Location filter */}
          <div className="relative">
            <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select value={selectedLoc} onChange={e => setSelectedLoc(e.target.value)}
              className="pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 appearance-none cursor-pointer hover:border-slate-300 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none">
              <option value="all">All Stores ({locations.length})</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          {/* Month nav */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-1 py-1">
            <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
              <ChevronLeft size={14} className="text-slate-500" />
            </button>
            <span className="px-3 text-xs font-bold text-slate-700 min-w-[120px] text-center">{monthLabel}</span>
            <button onClick={() => changeMonth(1)} disabled={isCurrentMonth}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-30">
              <ChevronRight size={14} className="text-slate-500" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-indigo-400" />
        </div>
      ) : (
        <>
          {/* ─── Summary Cards ─── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign size={12} className="text-emerald-500" />
                <span className="text-[10px] text-slate-400 font-bold uppercase">Total Sales</span>
              </div>
              <p className="text-lg font-black text-slate-900">QAR {formatCurrency(totalSales)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle2 size={12} className="text-emerald-500" />
                <span className="text-[10px] text-slate-400 font-bold uppercase">Compliance</span>
              </div>
              <p className={cn('text-lg font-black', complianceRate >= 80 ? 'text-emerald-600' : complianceRate >= 50 ? 'text-amber-600' : 'text-red-600')}>
                {complianceRate}%
              </p>
              <p className="text-[10px] text-slate-300">{actualEntries} of {expectedEntries} entries</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <XCircle size={12} className="text-red-400" />
                <span className="text-[10px] text-slate-400 font-bold uppercase">Missing Days</span>
              </div>
              <p className="text-lg font-black text-red-600">{missingDays.length}</p>
              <p className="text-[10px] text-slate-300">across {locsToCheck.length} branch{locsToCheck.length !== 1 ? 'es' : ''}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock size={12} className="text-amber-500" />
                <span className="text-[10px] text-slate-400 font-bold uppercase">Backfills</span>
              </div>
              <p className="text-lg font-black text-amber-600">{backfillCount}</p>
              <p className="text-[10px] text-slate-300">late entries this month</p>
            </div>
          </div>

          {/* ─── Calendar Heatmap ─── */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar size={14} className="text-indigo-500" /> Daily Entry Status
            </h3>

            {/* Day labels */}
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-[9px] font-bold text-slate-300 uppercase">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1.5">
              {/* Empty cells for offset */}
              {Array.from({ length: firstDayOfWeek }, (_, i) => (
                <div key={`empty-${i}`} />
              ))}

              {calendarDays.map(cd => (
                <motion.div
                  key={cd.day}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: cd.day * 0.01 }}
                  className={cn(
                    'relative rounded-lg p-1.5 text-center border transition-all cursor-default min-h-[52px]',
                    cd.isFuture
                      ? 'bg-slate-50 border-slate-100 opacity-40'
                      : cd.allFilled
                        ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-300'
                        : cd.someFilled
                          ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                          : 'bg-red-50 border-red-200 hover:border-red-300',
                    cd.isToday && 'ring-2 ring-indigo-400 ring-offset-1'
                  )}
                  title={cd.isFuture ? '' : `${cd.dateStr}: ${cd.actualCount}/${cd.expectedCount} branches, QAR ${formatCurrency(cd.daySales)}`}>
                  <p className={cn(
                    'text-[11px] font-bold',
                    cd.isFuture ? 'text-slate-300'
                      : cd.allFilled ? 'text-emerald-700'
                        : cd.someFilled ? 'text-amber-700'
                          : 'text-red-600'
                  )}>
                    {cd.day}
                  </p>
                  {!cd.isFuture && cd.daySales > 0 && (
                    <p className="text-[8px] font-bold text-slate-500 mt-0.5 truncate">{formatCurrency(cd.daySales)}</p>
                  )}
                  {!cd.isFuture && (
                    <div className="flex items-center justify-center gap-0.5 mt-0.5">
                      <span className={cn(
                        'text-[7px] font-bold',
                        cd.allFilled ? 'text-emerald-500' : cd.someFilled ? 'text-amber-500' : 'text-red-400'
                      )}>
                        {cd.actualCount}/{cd.expectedCount}
                      </span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-emerald-200 border border-emerald-300" />
                <span className="text-[10px] text-slate-400">All entered</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-amber-200 border border-amber-300" />
                <span className="text-[10px] text-slate-400">Partial</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-red-200 border border-red-300" />
                <span className="text-[10px] text-slate-400">Missing</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-slate-100 border border-slate-200" />
                <span className="text-[10px] text-slate-400">Future</span>
              </div>
            </div>
          </div>

          {/* ─── Missing Days Alert ─── */}
          {missingDays.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                <AlertTriangle size={14} /> Missing Entries ({missingDays.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
                {missingDays.slice(0, 30).map((m, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-red-100">
                    <XCircle size={10} className="text-red-400 shrink-0" />
                    <span className="text-[11px] font-bold text-slate-700 truncate">{m.locName}</span>
                    <span className="text-[10px] text-slate-400 ml-auto shrink-0">{m.date}</span>
                  </div>
                ))}
                {missingDays.length > 30 && (
                  <p className="text-[10px] text-red-400 font-bold col-span-full">…and {missingDays.length - 30} more</p>
                )}
              </div>
            </div>
          )}

          {/* ─── Entry Log Table ─── */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Entry Log</h3>
              <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-bold">
                {filteredEntries.length} entries
              </span>
            </div>
            {filteredEntries.length > 0 ? (
              <div className="max-h-[400px] overflow-y-auto">
                {/* Table header */}
                <div className="grid grid-cols-[1fr_100px_100px_80px_80px] gap-2 px-5 py-2 bg-slate-50 border-b border-slate-100 text-[9px] font-bold text-slate-400 uppercase tracking-wider sticky top-0">
                  <span>Branch</span>
                  <span>Date</span>
                  <span>Amount</span>
                  <span>Entered By</span>
                  <span>Status</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {filteredEntries.map((e, i) => {
                    const createdStr = e.created_at ? String(e.created_at).slice(0, 10) : '';
                    const isBackfill = createdStr && e.entry_date && createdStr !== e.entry_date;
                    return (
                      <div key={e.id || i}
                        className="grid grid-cols-[1fr_100px_100px_80px_80px] gap-2 px-5 py-2.5 items-center hover:bg-slate-50/60 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={cn('w-6 h-6 rounded-md flex items-center justify-center shrink-0', isBackfill ? 'bg-amber-50' : 'bg-emerald-50')}>
                            {isBackfill ? <Clock size={10} className="text-amber-500" /> : <DollarSign size={10} className="text-emerald-600" />}
                          </div>
                          <p className="text-xs font-bold text-slate-800 truncate">{e.location_name || 'Unknown'}</p>
                        </div>
                        <p className="text-[11px] text-slate-500 font-medium">{e.entry_date}</p>
                        <p className="text-xs font-black text-emerald-600">QAR {formatCurrency(Number(e.sales_amount ?? 0))}</p>
                        <p className="text-[10px] text-slate-400 truncate">{e.entered_name || '—'}</p>
                        <div>
                          {isBackfill ? (
                            <span className="text-[9px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded font-bold">Backfill</span>
                          ) : (
                            <span className="text-[9px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded font-bold">Live</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Calendar size={28} className="mx-auto text-slate-200 mb-3" />
                <p className="text-sm font-semibold text-slate-400">No entries for {monthLabel}.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
